import dayjs from "dayjs";
import Link from "next/link";
import "dayjs/locale/tr";
import BookmarkIcon from "@/components/icons/bookmark";
import LikeIcon from "@/components/icons/like";
import MessageIcon from "@/components/icons/message";
import ExtendedImage from "@/components/ui/extended-image";
import UserCard from "@/components/ui/user-card";
import PlayButton from "@/components/ui/video/play-button";
import { ShareButton } from "@/components/content/interactions";
import { cn } from "@/lib/utils";
import { ContentType } from "@/types/content";
import type { ContentCard } from "@/types/dashboard";

dayjs.locale("tr");

const SearchHorizontalContentCard = ({ content }: { content: ContentCard }) => {
  const showAuthor =
    content.author &&
    ![ContentType.Video, ContentType.Quiz].includes(content.type);

  return (
    <div className={cn("p-0 pb-6 md:pb-8 flex flex-row gap-3 md:gap-4 justify-between items-start")}>
      <div
        className={cn("flex flex-col gap-1.5 md:gap-2 w-full min-w-0", {
          "justify-between": !showAuthor,
          "justify-end": showAuthor,
        })}
      >
        {showAuthor && <UserCard user={content.author} />}
        {content.category && (
          <span className="text-[10px] md:text-xs leading-3.5 font-semibold text-neutral-500">
            {content.category?.name}
          </span>
        )}
        <Link className="hover:underline" href={`/${content.slug}`}>
          <h1 className="text-base sm:text-lg md:text-[26px] md:leading-7.5 font-bold line-clamp-2 md:line-clamp-3">
            {content.title}
          </h1>
        </Link>
        <p className="text-xs sm:text-sm leading-4 sm:leading-5 font-medium w-full line-clamp-2 mb-1 md:mb-2 text-neutral-600 md:text-neutral-900">
          {content.lead || content.summary || ""}
        </p>

        <div className="flex flex-row justify-between w-full items-center mt-auto md:mt-0">
          <div className="mt-1 flex flex-row items-center gap-2 md:gap-3 flex-wrap">
            <span className="text-[11px] sm:text-sm leading-5 font-semibold text-neutral-400">
              {dayjs(content.publishedAt).format("DD MMMM YYYY")}
            </span>
            <span className="inline-flex items-center justify-center gap-1 text-[11px] sm:text-sm font-semibold text-neutral-400">
              <Link className="group" href={`/${content.slug}`}>
                <LikeIcon className="size-4 sm:size-5 md:size-6 stroke-[1.5] stroke-neutral-400 group-hover:fill-neutral-300" />
              </Link>
              {content.likeCount}
            </span>
            <span className="inline-flex items-center justify-center gap-1 text-[11px] sm:text-sm font-semibold text-neutral-400">
              <Link className="group" href={`/${content.slug}`}>
                <MessageIcon className="stroke-neutral-400 size-4 sm:size-5 md:size-6 stroke-[1.5] group-hover:stroke-brand-blue-500" />
              </Link>
              {content.commentCount}
            </span>
          </div>
          <div className="flex flex-row gap-1 md:gap-2 justify-center items-center shrink-0">
            <Link className="group" href={`/${content.slug}`}>
              <BookmarkIcon className="size-4 sm:size-5 md:size-6 stroke-[1.5] stroke-neutral-400 fill-none group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100" />
            </Link>
            <ShareButton />
          </div>
        </div>
      </div>
      <div className="shrink-0 justify-center items-center flex flex-col relative w-40 sm:w-48 md:w-77 aspect-video md:aspect-auto md:h-43.5 rounded-lg md:rounded-2xl overflow-clip mt-1 md:mt-0">
        <Link className="hover:underline w-full h-full" href={`/${content.slug}`}>
          <ExtendedImage
            alt={`${content.title} image`}
            className="w-full h-full object-cover"
            height={174}
            src={content.coverImageUrl || (content.thumbnailUrl as string)}
            width={308}
          />
          {content.type === 1 && <PlayButton />}
        </Link>
      </div>
    </div>
  );
};

export default SearchHorizontalContentCard;
